import React from 'react';
import { animated, useSpring, useTransition } from 'react-spring';
import classnames from 'classnames';
import sortStyles from './sort.module.pcss';
import { MessageProperties, ListerSortData } from '../../../../types';
import ArrowDown from '../../../../../../svg/icons/arrow-down.svg';

export const BLUR_TARGET = 'BLUR_TARGET';
interface SortProps {
  messages: MessageProperties;
  isDisabled: boolean;
  sortOptions: ListerSortData[];
  isOpen: boolean;
  selectedOption: string;
  focusedOption: string;
  toggleOpen: () => void;
  handleEsc: (e: React.KeyboardEvent) => void;
  handleBlur: (e: React.FocusEvent<HTMLButtonElement>) => void;
  handleFocus: (optionCode: string) => void;
  handleChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleOptionClick: (option: ListerSortData) => void;
}

function Sort({
  isOpen,
  messages,
  isDisabled,
  sortOptions,
  selectedOption,
  focusedOption,
  toggleOpen,
  handleBlur,
  handleFocus,
  handleChange,
  handleEsc,
  handleOptionClick
}: SortProps): React.ReactElement {
  const listTransitions = useTransition(isOpen, null, {
    from: { opacity: 0, transform: 'scale(0.8)' },
    enter: { opacity: 1, transform: 'scale(1)' },
    leave: { opacity: 0, transform: 'scale(0.8)' },
    config: { duration: 200 }
  });

  const { transform } = useSpring({
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    config: { duration: 200 }
  });

  return (
    <div className={sortStyles.sort}>
      <span className={sortStyles.sort__title}>{messages.sortTitle}</span>
      <select
        disabled={isDisabled}
        onChange={handleChange}
        onKeyDown={handleEsc}
        value={selectedOption}
        className={sortStyles.sort__select}
      >
        {sortOptions.map((option) => (
          <option key={option.code} value={option.code}>
            {option.name}
          </option>
        ))}
      </select>

      <div className={sortStyles.listbox}>
        <button
          type="button"
          disabled={isDisabled}
          onClick={toggleOpen}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby="listboxTogglerLabel listboxTogglerSelected"
          className={sortStyles.listbox__toggler}
          data-blur={BLUR_TARGET}
          onBlur={handleBlur}
        >
          <span className={sortStyles['listbox__toggler-title']} id="listboxTogglerLabel">
            {messages.sortByTitle}
          </span>
          <span
            className={classnames(sortStyles['listbox__toggler-value'], {
              [sortStyles['listbox__toggler-value--expanded']]: isOpen
            })}
            id="listboxTogglerSelected"
          >
            {sortOptions.find((option) => option.code === selectedOption)?.name}
          </span>
          <animated.div className={sortStyles['listbox__toggler-arrow']} style={{ transform }}>
            <ArrowDown />
          </animated.div>
        </button>
        {listTransitions.map(
          ({ item, key, props }) =>
            item && (
              <animated.ul
                key={key}
                style={props}
                role="listbox"
                aria-activedescendant={focusedOption}
                className={sortStyles.listbox__options}
                aria-labelledby="listboxTogglerLabel"
                tabIndex={-1}
              >
                {sortOptions.map((option) => (
                  <li role="option" id={option.code} key={option.code} aria-selected={focusedOption === option.code}>
                    <button
                      type="button"
                      disabled={isDisabled}
                      data-blur={BLUR_TARGET}
                      className={sortStyles.listbox__option}
                      onFocus={(): void => handleFocus(option.code)}
                      onBlur={handleBlur}
                      onClick={(): void => handleOptionClick(option)}
                    >
                      <span className={sortStyles['listbox__option-text']}>{option.name}</span>
                    </button>
                  </li>
                ))}
              </animated.ul>
            )
        )}
      </div>
    </div>
  );
}

export default Sort;
